import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Dropdown, Header, Icon, Image,
  Input, Message, Modal, Segment
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props {
  targetTeamId: string;
  targetTeamName: string;
  targetTeamLogoUrl?: string;
  targetSportId: string;
}

export default observer(function ChallengeFromProfileModal({
  targetTeamId,
  targetTeamName,
  targetTeamLogoUrl,
  targetSportId,
}: Props) {
  const { teamStore, teamMatchStore } = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  // Guard: target team must not be one of my own teams
  const targetIsMyTeam = teamStore.myTeams.some(mt => mt.id === targetTeamId);

  const eligibleTeams = targetIsMyTeam
    ? []
    : teamStore.myTeams.filter(
        t =>
          t.status === 'Active' &&
          t.sportID === targetSportId &&
          t.id !== targetTeamId &&
          t.isCurrentUserCaptain
      );

  useEffect(() => {
    if (open && teamStore.myTeams.length === 0) {
      teamStore.loadMyTeams();
    }
  }, [open]);

  useEffect(() => {
    if (open && eligibleTeams.length === 1 && !selectedTeamId) {
      setSelectedTeamId(eligibleTeams[0].id);
    }
  }, [open, eligibleTeams.length]);

  const handleOpen = () => {
    setOpen(true);
    setScheduledAt('');
    setSelectedTeamId('');
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTeamId('');
    setScheduledAt('');
  };

  const handleSendChallenge = async () => {
    if (!selectedTeamId) return;
    const matchId = await teamMatchStore.createDirectChallenge({
      teamAId: selectedTeamId,
      teamBId: targetTeamId,
      scheduledAt: scheduledAt || undefined,
    });
    if (matchId) {
      handleClose();
      navigate(`/teammatches/${matchId}`);
    }
  };

  const dropdownOptions = eligibleTeams.map(team => ({
    key: team.id,
    value: team.id,
    text: team.name,
    image: {
      avatar: true,
      src: team.logoUrl || '/assets/team-placeholder.png',
    },
  }));

  const selectedTeam = eligibleTeams.find(t => t.id === selectedTeamId);

  return (
    <>
      <Button
        color="orange"
        size="small"
        style={{ borderRadius: 50 }}
        onClick={handleOpen}
      >
        <Icon name="shield alternate" />
        {t('challengeFromProfile.button')}
      </Button>

      <Modal open={open} onClose={handleClose} size="small" closeIcon>
        <Modal.Header>
          <Icon name="shield alternate" color="orange" />
          {t('challengeFromProfile.modalTitle')}
        </Modal.Header>

        <Modal.Content>
          {/* Target team display */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            background: '#fff7ed',
            border: '1.5px solid #fed7aa',
            borderRadius: 10,
            marginBottom: 20,
          }}>
            <Image
              src={targetTeamLogoUrl || '/assets/team-placeholder.png'}
              avatar
              style={{ width: 42, height: 42, objectFit: 'cover' }}
            />
            <div>
              <div style={{ fontSize: '0.78em', color: '#888', marginBottom: 2 }}>
                {t('challengeFromProfile.challengingTeam')}
              </div>
              <div style={{ fontWeight: 700, fontSize: '1.05em' }}>{targetTeamName}</div>
            </div>
          </div>

          {/* No eligible teams */}
          {eligibleTeams.length === 0 && (
            <Message warning>
              <Icon name="exclamation triangle" />
              {t('challengeFromProfile.noEligibleTeams')}
            </Message>
          )}

          {/* Team picker */}
          {eligibleTeams.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontWeight: 700,
                marginBottom: 6,
                fontSize: '0.9em',
              }}>
                {t('challengeFromProfile.selectYourTeam')}
              </label>

              {eligibleTeams.length === 1 ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 12px',
                  background: '#f0fdf4',
                  border: '1.5px solid #22c55e',
                  borderRadius: 10,
                }}>
                  <Image
                    src={eligibleTeams[0].logoUrl || '/assets/team-placeholder.png'}
                    avatar
                    style={{ width: 32, height: 32, objectFit: 'cover' }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95em' }}>
                      {eligibleTeams[0].name}
                    </div>
                    <div style={{ fontSize: '0.75em', color: '#555' }}>
                      <Icon name="user" size="small" />
                      {eligibleTeams[0].teamMembers.length} {t('teams.members')}
                    </div>
                  </div>
                  <Icon
                    name="check circle"
                    color="green"
                    style={{ marginLeft: 'auto' }}
                  />
                </div>
              ) : (
                <Dropdown
                  fluid
                  selection
                  placeholder={t('challengeFromProfile.selectTeamPlaceholder')}
                  options={dropdownOptions}
                  value={selectedTeamId}
                  onChange={(_, d) => setSelectedTeamId(d.value as string)}
                />
              )}
            </div>
          )}

          {/* VS preview */}
          {selectedTeam && (
            <Segment style={{ textAlign: 'center', background: '#f9fafb', marginBottom: 20 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 20,
                flexWrap: 'wrap',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Image
                    src={selectedTeam.logoUrl || '/assets/team-placeholder.png'}
                    circular centered
                    style={{ width: 50, height: 50, objectFit: 'cover', margin: '0 auto 6px' }}
                  />
                  <div style={{ fontWeight: 700, fontSize: '0.88em' }}>{selectedTeam.name}</div>
                </div>
                <div style={{
                  fontWeight: 900,
                  fontSize: '1.3em',
                  color: '#f97316',
                  padding: '4px 12px',
                  background: '#fff7ed',
                  borderRadius: 8,
                  border: '1px solid #fed7aa',
                }}>
                  VS
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Image
                    src={targetTeamLogoUrl || '/assets/team-placeholder.png'}
                    circular centered
                    style={{ width: 50, height: 50, objectFit: 'cover', margin: '0 auto 6px' }}
                  />
                  <div style={{ fontWeight: 700, fontSize: '0.88em' }}>{targetTeamName}</div>
                </div>
              </div>
            </Segment>
          )}

          {/* Scheduled date/time */}
          {eligibleTeams.length > 0 && (
            <div>
              <label style={{
                display: 'block',
                fontWeight: 700,
                marginBottom: 6,
                fontSize: '0.9em',
              }}>
                {t('challengeFromProfile.scheduledAt')}
              </label>
              <Input
                type="datetime-local"
                value={scheduledAt}
                onChange={e => setScheduledAt(e.target.value)}
                fluid
              />
            </div>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button basic onClick={handleClose} disabled={teamMatchStore.submitting}>
            {t('teams.cancel')}
          </Button>
          {eligibleTeams.length > 0 && (
            <Button
              color="orange"
              loading={teamMatchStore.submitting}
              disabled={!selectedTeamId || teamMatchStore.submitting}
              onClick={handleSendChallenge}
            >
              <Icon name="send" />
              {t('challengeFromProfile.sendChallenge')}
            </Button>
          )}
        </Modal.Actions>
      </Modal>
    </>
  );
});